import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Buildings, GraduationCap, Plus, Trash, UserPlus, X, PencilSimple, DeviceMobile, ShieldCheck } from "@phosphor-icons/react";
import { roleLabel } from "@/lib/roles";
import { PurgeRow, CertHistory } from "@/components/admin/SchoolPurgeCard";
import { SchoolsPricingCard } from "@/components/admin/SchoolsPricingCard";
import { CurriculumAlignmentCard } from "@/components/admin/CurriculumAlignmentCard";
import { useAuth } from "@/contexts/AuthContext";

// Config for the three assignable staff kinds. School admins & teachers share the school
// endpoints (the list returns both, filtered client-side by role); board admins use board endpoints.
const STAFF = {
  board_admin: {
    key: "board-admin", noun: "board admin", nounPlural: "board admins", label: "Board Admins",
    roleKeys: ["school_board_admin"], createRole: "school_board_admin",
    listUrl: (id) => `/boards/${id}/admins`, assignUrl: (id) => `/boards/${id}/admins`, unassignBase: (id) => `/boards/${id}/admins`,
  },
  school_admin: {
    key: "school-admin", noun: "school admin", nounPlural: "school admins", label: "School Admins",
    roleKeys: ["school_admin"], createRole: "school_admin",
    listUrl: (id) => `/schools/${id}/teachers`, assignUrl: (id) => `/schools/${id}/teachers`, unassignBase: (id) => `/schools/${id}/teachers`,
  },
  teacher: {
    key: "teacher", noun: "teacher", nounPlural: "teachers", label: "Teachers",
    roleKeys: ["teacher"], createRole: "teacher",
    listUrl: (id) => `/schools/${id}/teachers`, assignUrl: (id) => `/schools/${id}/teachers`, unassignBase: (id) => `/schools/${id}/teachers`,
  },
};

// Sentinel for the "Independent schools" pseudo-board (private schools with no board).
const INDEPENDENT = "__independent__";

// Super-admin-only: PWA install counts per school (endpoint 403s for anyone else).
function InstallStatsCard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/pwa-install-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;
  return (
    <div className="rounded-xl border border-[#C4C8CE] bg-white p-4 mb-5" data-testid="install-stats-card">
      <div className="flex items-center gap-2 font-display font-bold mb-2">
        <DeviceMobile size={18} weight="duotone" color="#84CC16" /> App installs
        <span className="text-xs font-sans font-normal text-[#000000]">— devices running the installed app (your eyes only)</span>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">
        <div data-testid="install-stats-total"><span className="font-bold text-lg">{stats.total_devices}</span> devices · <span className="font-bold">{stats.total_users}</span> users total</div>
      </div>
      {(stats.schools.length > 0 || stats.no_school.devices > 0) && (
        <table className="mt-2 text-sm w-full max-w-md">
          <tbody>
            {stats.schools.map((s) => (
              <tr key={s.school_id} className="border-t border-[#E4E4E7]" data-testid={`install-stats-school-${s.school_id}`}>
                <td className="py-1 pr-3">{s.school_name}</td>
                <td className="py-1 text-right font-bold">{s.devices} device{s.devices === 1 ? "" : "s"}</td>
                <td className="py-1 pl-3 text-right text-[#000000]">{s.users} user{s.users === 1 ? "" : "s"}</td>
              </tr>
            ))}
            {stats.no_school.devices > 0 && (
              <tr className="border-t border-[#E4E4E7]" data-testid="install-stats-no-school">
                <td className="py-1 pr-3 text-[#000000]">Public / no school</td>
                <td className="py-1 text-right font-bold">{stats.no_school.devices} device{stats.no_school.devices === 1 ? "" : "s"}</td>
                <td className="py-1 pl-3 text-right text-[#000000]">{stats.no_school.users} user{stats.no_school.users === 1 ? "" : "s"}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {stats.total_devices === 0 && <div className="text-sm text-[#000000] mt-1">No installs recorded yet — counts appear as students install the app.</div>}
    </div>
  );
}


const PROVINCES = [
  ["AB", "Alberta"], ["BC", "British Columbia"], ["MB", "Manitoba"], ["NB", "New Brunswick"],
  ["NL", "Newfoundland and Labrador"], ["NS", "Nova Scotia"], ["NT", "Northwest Territories"],
  ["NU", "Nunavut"], ["ON", "Ontario"], ["PE", "Prince Edward Island"], ["QC", "Quebec"],
  ["SK", "Saskatchewan"], ["YT", "Yukon"],
];

// Super-admin overview: which boards still lack an AI Coach consent record. Boards running
// Coach without a sign-off are flagged amber; clicking a chip opens that board to record it.
function CoachSignoffOverview({ boards, onOpenBoard }) {
  if (!boards.length) return null;
  const signed = boards.filter((b) => b.coach_signoff);
  const pending = boards.filter((b) => !b.coach_signoff);
  return (
    <div className="rounded-xl border border-[#C4C8CE] bg-white p-4 mb-5" data-testid="coach-signoff-overview">
      <div className="flex items-center gap-2 font-display font-bold mb-1">
        <ShieldCheck size={18} weight="duotone" color="#84CC16" /> AI Coach sign-offs
        <span className="text-xs font-sans font-normal text-[#000000]">
          — {signed.length} of {boards.length} board{boards.length === 1 ? "" : "s"} have a consent record
        </span>
      </div>
      {pending.length === 0 ? (
        <div className="text-sm text-[#4D7C0F]" data-testid="coach-signoff-all-signed">✓ Every board has signed off on the AI Coach.</div>
      ) : (
        <>
          <div className="text-xs text-[#52525B] mb-2">Still waiting on sign-off — click a board to open it and record one:</div>
          <div className="flex flex-wrap gap-2" data-testid="coach-signoff-pending-list">
            {pending.map((b) => {
              const onNoProof = b.coach_enabled !== false;
              return (
                <button key={b.id} data-testid={`signoff-pending-${b.id}`} onClick={() => onOpenBoard(b.id)}
                  className="text-xs font-bold px-3 py-1.5 rounded-full border cursor-pointer bg-white"
                  style={{ borderColor: onNoProof ? "#F59E0B" : "#C4C8CE", color: onNoProof ? "#92400E" : "#3F3F46" }}
                  title={onNoProof ? "Coach is ON for this board but no consent record exists yet" : "Coach is OFF — pending sign-off"}>
                  {b.name} — {onNoProof ? "ON without sign-off" : "Coach OFF"}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}


export default function SchoolsPanel() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [boards, setBoards] = useState([]);
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [err, setErr] = useState("");
  const [bName, setBName] = useState(""); const [bProv, setBProv] = useState("");
  const [bAdmFirst, setBAdmFirst] = useState(""); const [bAdmLast, setBAdmLast] = useState(""); const [bAdmEmail, setBAdmEmail] = useState(""); const [bAdmPw, setBAdmPw] = useState("Welcome@123");
  const [sName, setSName] = useState("");
  const [sAdmFirst, setSAdmFirst] = useState(""); const [sAdmLast, setSAdmLast] = useState(""); const [sAdmEmail, setSAdmEmail] = useState(""); const [sAdmPw, setSAdmPw] = useState("Welcome@123");
  const [busyCreate, setBusyCreate] = useState(false);
  const [showBoardForm, setShowBoardForm] = useState(false);
  const [showSchoolForm, setShowSchoolForm] = useState(false);
  const [editing, setEditing] = useState(null); // { type:'board'|'school', entity }

  const load = async () => {
    setErr("");
    try {
      const [b, u] = await Promise.all([api.get("/boards"), api.get("/admin/users")]);
      setBoards(b.data); setUsers(u.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  const loadSchools = async (boardId) => {
    setSelectedBoard(boardId); setSelectedSchool(null); setShowSchoolForm(false);
    try { const q = boardId === INDEPENDENT ? "none" : boardId; const { data } = await api.get(`/schools?board_id=${q}`); setSchools(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  // Refresh the school list without collapsing the current selection (used after logo changes)
  const refreshSchools = async () => {
    if (!selectedBoard) return;
    try {
      const q = selectedBoard === INDEPENDENT ? "none" : selectedBoard;
      const { data } = await api.get(`/schools?board_id=${q}`);
      setSchools(data);
    } catch (_) {}
  };
  // Click a board again (or the same one) to collapse it back to its small state.
  const onBoardClick = (id) => {
    if (selectedBoard === id) { setSelectedBoard(null); setSchools([]); setSelectedSchool(null); setShowSchoolForm(false); }
    else loadSchools(id);
  };
  // Refresh school/board counts WITHOUT clearing the current school selection.
  const refreshCounts = async () => {
    try {
      const [b, u] = await Promise.all([api.get("/boards"), api.get("/admin/users")]);
      setBoards(b.data); setUsers(u.data);
      if (selectedBoard) { const q = selectedBoard === INDEPENDENT ? "none" : selectedBoard; const { data } = await api.get(`/schools?board_id=${q}`); setSchools(data); }
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  // Board admins have exactly one board — open it right away.
  useEffect(() => {
    if (!isSuper && boards.length === 1 && !selectedBoard) loadSchools(boards[0].id);
    // eslint-disable-next-line
  }, [boards]);

  const createBoard = async (e) => {
    e.preventDefault();
    setErr(""); setBusyCreate(true);
    try {
      const { data: board } = await api.post("/boards", { name: bName.trim(), province: bProv });
      if (bAdmFirst.trim() && bAdmEmail.trim()) {
        const { data: admin } = await api.post("/admin/users", { first_name: bAdmFirst.trim(), last_name: bAdmLast.trim() || null, email: bAdmEmail.trim().toLowerCase(), password: bAdmPw, role: "school_board_admin" });
        await api.post(`/boards/${board.id}/admins`, { user_id: admin.id });
      }
      setBName(""); setBProv(""); setBAdmFirst(""); setBAdmLast(""); setBAdmEmail(""); setBAdmPw("Welcome@123");
      setShowBoardForm(false);
      await load();
      loadSchools(board.id);   // select the new board so its admin(s) show for editing
      toast.success(`Board "${board.name}" created`);
    } catch (e) { const m = formatApiError(e); setErr(m); toast.error(m); }
    finally { setBusyCreate(false); }
  };
  const createSchool = async (e) => {
    e.preventDefault();
    if (!selectedBoard) { setErr("Select a board first"); return; }
    setErr(""); setBusyCreate(true);
    try {
      const board_id = selectedBoard === INDEPENDENT ? null : selectedBoard;
      const { data: school } = await api.post("/schools", { name: sName.trim(), board_id });
      if (sAdmFirst.trim() && sAdmEmail.trim()) {
        const { data: admin } = await api.post("/admin/users", { first_name: sAdmFirst.trim(), last_name: sAdmLast.trim() || null, email: sAdmEmail.trim().toLowerCase(), password: sAdmPw, role: "school_admin" });
        await api.post(`/schools/${school.id}/teachers`, { user_id: admin.id });
      }
      setSName(""); setSAdmFirst(""); setSAdmLast(""); setSAdmEmail(""); setSAdmPw("Welcome@123");
      setShowSchoolForm(false);
      await loadSchools(selectedBoard);
      setSelectedSchool(school);   // select the new school so its admin(s) show for editing
      await load();
      toast.success(`School "${school.name}" created`);
    } catch (e) { const m = formatApiError(e); setErr(m); toast.error(m); }
    finally { setBusyCreate(false); }
  };
  const delBoard = async (id) => { if (!window.confirm("Delete this board?")) return; try { await api.delete(`/boards/${id}`); if (selectedBoard === id) { setSelectedBoard(null); setSchools([]); } load(); } catch (e) { setErr(formatApiError(e)); } };
  const delSchool = async (id) => { if (!window.confirm("Delete this school?")) return; try { await api.delete(`/schools/${id}`); loadSchools(selectedBoard); load(); } catch (e) { setErr(formatApiError(e)); } };

  const saveEdit = async (fields) => {
    const { type, entity } = editing;
    try {
      if (type === "board") { await api.patch(`/boards/${entity.id}`, fields); load(); }
      else { await api.patch(`/schools/${entity.id}`, fields); loadSchools(selectedBoard); }
      setEditing(null);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div data-testid="schools-panel">
      {isSuper && <InstallStatsCard />}
      {isSuper && <SchoolsPricingCard />}
      {isSuper && <CurriculumAlignmentCard />}
      {isSuper && <CoachSignoffOverview boards={boards} onOpenBoard={loadSchools} />}
      {err && <div className="text-[#FF5100] mb-3 text-sm" data-testid="schools-error">{err}</div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Boards */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 font-display font-bold"><Buildings size={18} weight="duotone" color="#84CC16" /> School Boards</div>
            {isSuper && (
              <button data-testid="toggle-board-form" onClick={() => setShowBoardForm((v) => !v)} title={showBoardForm ? "Close" : "Create board"}
                className="w-7 h-7 rounded-full bg-[#CCFF00] text-[#000000] flex items-center justify-center hover:brightness-95 transition-all shrink-0">
                {showBoardForm ? <X size={15} weight="bold" /> : <Plus size={15} weight="bold" />}
              </button>
            )}
          </div>
          {showBoardForm && (
            <form onSubmit={createBoard} className="rounded-xl border border-[#C4C8CE] bg-white p-3 mb-3 flex flex-col gap-2">
              <input data-testid="board-name-input" className="input-base w-full" placeholder="Board name" required value={bName} onChange={(e) => setBName(e.target.value)} />
              <select data-testid="board-province-input" className="input-base w-full" required value={bProv} onChange={(e) => setBProv(e.target.value)}>
                <option value="">Province (required — Coach uses it for legal jurisdiction)</option>
                {PROVINCES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
              <div className="pt-2 mt-1 border-t border-[#E4E4E7]">
                <div className="text-[11px] uppercase tracking-wider text-[#000000] mb-1.5">Main contact / admin (optional)</div>
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="board-admin-first-name" className="input-base w-full" placeholder="First name" value={bAdmFirst} onChange={(e) => setBAdmFirst(e.target.value)} />
                    <input data-testid="board-admin-last-name" className="input-base w-full" placeholder="Last name" value={bAdmLast} onChange={(e) => setBAdmLast(e.target.value)} />
                  </div>
                  <input data-testid="board-admin-email" type="email" className="input-base w-full" placeholder="Admin email" value={bAdmEmail} onChange={(e) => setBAdmEmail(e.target.value)} />
                  <input data-testid="board-admin-password" className="input-base w-full" placeholder="Temp password" value={bAdmPw} onChange={(e) => setBAdmPw(e.target.value)} />
                </div>
              </div>
              <button data-testid="create-board-submit" disabled={busyCreate || !bName.trim() || !bProv} className="btn-primary w-full flex items-center justify-center gap-1.5 disabled:opacity-50">
                <Plus size={15} weight="bold" /> {busyCreate ? "Creating…" : "Create board"}
              </button>
            </form>
          )}
          <div className="space-y-2">
            {boards.map((b) => {
              const sel = selectedBoard === b.id;
              return (
                <div key={b.id} data-testid={`board-item-${b.id}`} className="rounded-xl border overflow-hidden"
                  style={{ borderColor: sel ? "#0A0D14" : "#C4C8CE" }}>
                  <div onClick={() => onBoardClick(b.id)} className="p-2.5 cursor-pointer flex items-center justify-between"
                    style={{ background: sel ? "#0A0D14" : "#fff", color: sel ? "#fff" : "#0A0D14" }}>
                    <div><div className="font-semibold">{b.name}</div><div className="text-xs opacity-70">{b.province || "—"} · {b.school_count} school{b.school_count === 1 ? "" : "s"}</div></div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button data-testid={`edit-board-${b.id}`} onClick={(e) => { e.stopPropagation(); setEditing({ type: "board", entity: b }); }} title="Edit board" style={{ color: sel ? "#CCFF00" : "#52525B" }}><PencilSimple size={15} /></button>
                      {isSuper && <button data-testid={`delete-board-${b.id}`} onClick={(e) => { e.stopPropagation(); delBoard(b.id); }} className="text-[#FF5100]"><Trash size={15} /></button>}
                    </div>
                  </div>
                  {sel && (
                    <div className="p-2.5 bg-[#F7F7F5] border-t border-[#C4C8CE]">
                      <BoardLogoRow board={b} onChanged={load} />
                      <ShareAchievementToggle board={b} onChanged={load} />
                      <CoachToggle board={b} onChanged={load} />
                      <StaffAssign scopeId={b.id} scopeName={b.name} kind={STAFF.board_admin} users={users} onChanged={refreshCounts} embedded canCreate={isSuper} />
                      <PurgeRow kind="board" entity={b} onDone={load} />
                    </div>
                  )}
                </div>
              );
            })}
            {boards.length === 0 && <div className="text-sm text-[#000000]">{isSuper ? "No boards yet." : "You aren't assigned to a board yet."}</div>}
            {/* Independent / private schools that sit directly under you (no board) */}
            {isSuper && (
            <div data-testid="independent-schools-card" onClick={() => onBoardClick(INDEPENDENT)}
              className="rounded-xl border border-dashed p-2.5 cursor-pointer flex items-center justify-between transition-colors"
              style={{ background: selectedBoard === INDEPENDENT ? "#0A0D14" : "#fff", color: selectedBoard === INDEPENDENT ? "#fff" : "#0A0D14", borderColor: selectedBoard === INDEPENDENT ? "#0A0D14" : "#C4C8CE" }}>
              <div><div className="font-semibold">Independent schools</div><div className="text-xs opacity-70">Private · no school board</div></div>
              <Buildings size={16} weight="duotone" style={{ color: selectedBoard === INDEPENDENT ? "#CCFF00" : "#84CC16" }} />
            </div>
            )}
          </div>
        </div>

        {/* Schools */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 font-display font-bold"><GraduationCap size={18} weight="duotone" color="#84CC16" /> Schools</div>
            {selectedBoard && (
              <button data-testid="toggle-school-form" onClick={() => setShowSchoolForm((v) => !v)} title={showSchoolForm ? "Close" : "Create school"}
                className="w-7 h-7 rounded-full bg-[#CCFF00] text-[#000000] flex items-center justify-center hover:brightness-95 transition-all shrink-0">
                {showSchoolForm ? <X size={15} weight="bold" /> : <Plus size={15} weight="bold" />}
              </button>
            )}
          </div>
          {!selectedBoard ? (
            <div className="text-sm text-[#000000] rounded-xl border border-dashed border-[#C4C8CE] p-4">Select a board to view its schools.</div>
          ) : (
            <>
              {showSchoolForm && (
                <form onSubmit={createSchool} className="rounded-xl border border-[#C4C8CE] bg-white p-3 mb-3 flex flex-col gap-2">
                  <input data-testid="school-name-input" className="input-base w-full" placeholder="School name" required value={sName} onChange={(e) => setSName(e.target.value)} />
                  <div className="pt-2 mt-1 border-t border-[#E4E4E7]">
                    <div className="text-[11px] uppercase tracking-wider text-[#000000] mb-1.5">Main contact / admin (optional)</div>
                    <div className="flex flex-col gap-2">
                      <div className="grid grid-cols-2 gap-2">
                        <input data-testid="school-admin-first-name" className="input-base w-full" placeholder="First name" value={sAdmFirst} onChange={(e) => setSAdmFirst(e.target.value)} />
                        <input data-testid="school-admin-last-name" className="input-base w-full" placeholder="Last name" value={sAdmLast} onChange={(e) => setSAdmLast(e.target.value)} />
                      </div>
                      <input data-testid="school-admin-email" type="email" className="input-base w-full" placeholder="Admin email" value={sAdmEmail} onChange={(e) => setSAdmEmail(e.target.value)} />
                      <input data-testid="school-admin-password" className="input-base w-full" placeholder="Temp password" value={sAdmPw} onChange={(e) => setSAdmPw(e.target.value)} />
                    </div>
                  </div>
                  <button data-testid="create-school-submit" disabled={busyCreate || !sName.trim()} className="btn-primary w-full flex items-center justify-center gap-1.5 disabled:opacity-50">
                    <Plus size={15} weight="bold" /> {busyCreate ? "Creating…" : "Create school"}
                  </button>
                </form>
              )}
              <div className="space-y-2">
                {schools.map((s) => {
                  const sel = selectedSchool?.id === s.id;
                  return (
                    <div key={s.id} data-testid={`school-item-${s.id}`} className="rounded-xl border overflow-hidden"
                      style={{ borderColor: sel ? "#0A0D14" : "#C4C8CE" }}>
                      <div onClick={() => setSelectedSchool(s)} className="p-2.5 cursor-pointer flex items-center justify-between"
                        style={{ background: sel ? "#0A0D14" : "#fff", color: sel ? "#fff" : "#0A0D14" }}>
                        <div><div className="font-semibold">{s.name}</div><div className="text-xs opacity-70">{s.teacher_count} teacher{s.teacher_count === 1 ? "" : "s"}</div></div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button data-testid={`edit-school-${s.id}`} onClick={(e) => { e.stopPropagation(); setEditing({ type: "school", entity: s }); }} title="Edit school" style={{ color: sel ? "#CCFF00" : "#52525B" }}><PencilSimple size={15} /></button>
                          <button data-testid={`delete-school-${s.id}`} onClick={(e) => { e.stopPropagation(); delSchool(s.id); }} className="text-[#FF5100]"><Trash size={15} /></button>
                        </div>
                      </div>
                      {sel && (
                        <div className="p-2.5 bg-[#F7F7F5] border-t border-[#C4C8CE]">
                          <SchoolLogoRow school={s} onChanged={refreshSchools} />
                          <StaffAssign scopeId={s.id} scopeName={s.name} kind={STAFF.school_admin} users={users} onChanged={refreshCounts} embedded
                            fallback={<SchoolAdminFallback school={s} users={users} />} />
                          <PurgeRow kind="school" entity={s} onDone={refreshSchools} />
                        </div>
                      )}
                    </div>
                  );
                })}
                {schools.length === 0 && <div className="text-sm text-[#000000]">No schools in this board yet.</div>}
              </div>
            </>
          )}
        </div>

        {/* Teachers in selected school */}
        <div>
          <div className="flex items-center gap-2 mb-2 font-display font-bold"><UserPlus size={18} weight="duotone" color="#84CC16" /> Teachers</div>
          {!selectedSchool ? (
            <div className="text-sm text-[#000000] rounded-xl border border-dashed border-[#C4C8CE] p-4">Select a school to manage its teachers.</div>
          ) : (
            <StaffAssign scopeId={selectedSchool.id} scopeName={selectedSchool.name} kind={STAFF.teacher} users={users} onChanged={refreshCounts} />
          )}
        </div>
      </div>
      <CertHistory />

      {editing && <EditEntityModal editing={editing} onClose={() => setEditing(null)} onSave={saveEdit} />}
    </div>
  );
}

function SchoolLogoRow({ school, onChanged }) {
  return (
    <BrandLogoRow
      entity={school}
      base={`/schools/${school.id}/logo`}
      testPrefix="school-logo"
      hintSet="Overrides the platform logo for this school"
      hintUnset="Upload to brand the app & certificates for this school"
      toastSet="School logo set — it now replaces the platform logo for this school's staff & students (app + certificates)."
      toastRemoved="School logo removed — back to the platform logo."
      onChanged={onChanged}
    />
  );
}
export { SchoolLogoRow };

function BoardLogoRow({ board, onChanged }) {
  return (
    <BrandLogoRow
      entity={board}
      base={`/boards/${board.id}/logo`}
      testPrefix="board-logo"
      hintSet="Shown on certificates for board staff; fallback for schools without a logo"
      hintUnset="Upload to brand certificates for this board"
      toastSet="Board logo set — it appears on certificates for board staff and any school without its own logo."
      toastRemoved="Board logo removed."
      onChanged={onChanged}
    />
  );
}

function BrandLogoRow({ entity, base, testPrefix, hintSet, hintUnset, toastSet, toastRemoved, onChanged }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(base, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(toastSet);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(base);
      toast.success(toastRemoved);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 mb-3 rounded-lg bg-white border border-[#E4E4E7] px-2.5 py-2" data-testid={`${testPrefix}-row-${entity.id}`}>
      <div className="w-9 h-9 rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] grid place-items-center overflow-hidden shrink-0">
        {entity.logo_url ? <img src={entity.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" /> : <Buildings size={16} color="#A1A1AA" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold">{testPrefix === "board-logo" ? "Board logo (certificates)" : "White-label logo"}</div>
        <div className="text-[10px] text-[#000000] truncate">{entity.logo_url ? hintSet : hintUnset}</div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`${testPrefix}-input-${entity.id}`}
        onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      <button data-testid={`${testPrefix}-upload-${entity.id}`} disabled={busy} onClick={() => inputRef.current?.click()}
        className="btn-secondary text-xs leading-none shrink-0" style={{ padding: "4px 10px" }}>{busy ? "…" : entity.logo_url ? "Replace" : "Upload"}</button>
      {entity.logo_url && (
        <button data-testid={`${testPrefix}-remove-${entity.id}`} disabled={busy} onClick={remove}
          className="text-[#FF5100] shrink-0" title="Remove logo"><Trash size={14} /></button>
      )}
    </div>
  );
}

function EditEntityModal({ editing, onClose, onSave }) {
  const { type, entity } = editing;
  const [name, setName] = useState(entity.name || "");
  const [province, setProvince] = useState(entity.province || "");
  const [extra, setExtra] = useState(type === "board" ? (entity.country || "") : (entity.region || ""));
  const [langSel, setLangSel] = useState(type === "board" ? (entity.language || "en") : (entity.language || "inherit"));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fields = type === "board"
      ? { name: name.trim(), province: province.trim(), country: extra.trim(), language: langSel }
      : { name: name.trim(), province: province.trim(), region: extra.trim(), language: langSel };
    await onSave(fields);
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="edit-entity-modal" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7]">
          <div className="font-bold font-display">Edit {type === "board" ? "board" : "school"}</div>
          <button type="button" data-testid="edit-entity-close" onClick={onClose} className="btn-ghost"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <label className="text-xs uppercase tracking-wider text-[#000000]">Name
            <input data-testid="edit-name-input" className="input-base w-full mt-1" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="text-xs uppercase tracking-wider text-[#000000]">Province
            {type === "board" ? (
              <select data-testid="edit-province-input" className="input-base w-full mt-1" required value={province} onChange={(e) => setProvince(e.target.value)}>
                <option value="">Select province (required)</option>
                {PROVINCES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                {province && !PROVINCES.some(([c]) => c === province) && <option value={province}>{province}</option>}
              </select>
            ) : (
              <input data-testid="edit-province-input" className="input-base w-full mt-1" value={province} onChange={(e) => setProvince(e.target.value)} placeholder="optional" />
            )}
          </label>
          <label className="text-xs uppercase tracking-wider text-[#000000]">{type === "board" ? "Country" : "Region"}
            <input data-testid="edit-extra-input" className="input-base w-full mt-1" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="optional" />
          </label>
          <label className="text-xs uppercase tracking-wider text-[#000000]">Course language
            <select data-testid="edit-language-input" className="input-base w-full mt-1" value={langSel} onChange={(e) => setLangSel(e.target.value)}>
              {type === "school" && <option value="inherit">Inherit from board</option>}
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
            <span className="block normal-case tracking-normal text-[11px] text-[#52525B] mt-1">
              {type === "board"
                ? "Default for every school in this board — each school can override it. Students see the course in this language (they can still switch a lesson)."
                : "Overrides the board default for this school's students."}
            </span>
          </label>
          <button data-testid="edit-entity-save" type="submit" disabled={busy || !name.trim() || (type === "board" && !province)} className="btn-primary w-full">{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </form>
    </div>
  );
}

function SchoolAdminFallback({ school, users }) {
  const boardAdmins = school.board_id
    ? users.filter((u) => (u.roles || [u.role]).includes("school_board_admin") && u.school_board_id === school.board_id)
    : [];
  return (
    <div data-testid="school-admin-fallback" className="text-xs text-[#000000] rounded-lg bg-[#FEF9C3] border border-[#FDE68A] px-2.5 py-2 leading-relaxed">
      {school.board_id ? (
        boardAdmins.length > 0 ? (
          <>No school admin yet — all school-admin duties pass to the board admin{boardAdmins.length > 1 ? "s" : ""}: <b className="text-[#3F6212]">{boardAdmins.map((a) => a.name).join(", ")}</b>.</>
        ) : (
          <>No school admin <i>or</i> board admin yet — duties pass up to the Super Admin until one is assigned.</>
        )
      ) : (
        <>Independent school (no board) — with no school admin, duties pass directly to the Super Admin.</>
      )}
    </div>
  );
}

function StaffAssign({ scopeId, scopeName, kind, users, onChanged, embedded, fallback, canCreate = true }) {
  const cfg = kind;
  const [assigned, setAssigned] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(cfg.listUrl(scopeId));
      // The school list endpoint returns teachers + school admins mixed — keep only this kind's roles.
      setAssigned(data.filter((u) => (u.roles || [u.role]).some((r) => cfg.roleKeys.includes(r))));
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [scopeId, cfg.key]);

  const eligible = users.filter((u) => (u.roles || [u.role]).some((r) => cfg.roleKeys.includes(r)) && !assigned.find((a) => a.id === u.id));

  // Assign immediately on dropdown selection — no separate "+" click needed.
  const assign = async (userId) => {
    if (!userId) return;
    setErr(""); setBusy(true);
    const picked = eligible.find((u) => u.id === userId);
    try {
      await api.post(cfg.assignUrl(scopeId), { user_id: userId });
      await load(); onChanged();
      toast.success(`${picked ? picked.name : cfg.noun} added to ${scopeName}`);
    } catch (e) { const m = formatApiError(e); setErr(m); toast.error(m); }
    finally { setBusy(false); }
  };
  const unassign = async (id) => { setErr(""); try { await api.delete(`${cfg.unassignBase(scopeId)}/${id}`); await load(); onChanged(); } catch (e) { setErr(formatApiError(e)); } };
  const createAndAssign = async (fields) => {
    const { data } = await api.post("/admin/users", fields);   // may throw -> caught inline
    await api.post(cfg.assignUrl(scopeId), { user_id: data.id });
    await load(); onChanged();
  };

  return (
    <div className={embedded ? "" : "rounded-xl border border-[#C4C8CE] bg-white p-3"} data-testid={`staff-assign-${cfg.key}`}>
      <div className="font-semibold mb-2 text-sm">{cfg.label}</div>
      {err && <div className="text-[#FF5100] text-xs mb-2" data-testid={`${cfg.key}-assign-error`}>{err}</div>}
      <div className="mb-2">
        <select data-testid={`assign-${cfg.key}-select`} className="input-base w-full disabled:opacity-50" value="" disabled={busy || eligible.length === 0}
          onChange={(e) => assign(e.target.value)}>
          <option value="">{eligible.length === 0 ? `No ${cfg.nounPlural} available` : (busy ? "Adding…" : `＋ Add an existing ${cfg.noun}…`)}</option>
          {eligible.map((u) => <option key={u.id} value={u.id}>{u.name} ({roleLabel(u.primary_role || u.role)})</option>)}
        </select>
      </div>
      {canCreate && (
        <button data-testid={`new-${cfg.key}-btn`} onClick={() => setShowCreate((v) => !v)}
          className="w-full mb-3 rounded-lg border border-dashed border-[#84CC16] text-[#3F6212] bg-[#F7FEE7] hover:bg-[#ECFCCB] text-sm font-semibold py-1.5 flex items-center justify-center gap-1.5 transition-colors">
          <UserPlus size={15} weight="bold" /> {showCreate ? "Close form" : `Create new ${cfg.noun} here`}
        </button>
      )}
      {canCreate && showCreate && <CreateStaffInline cfg={cfg} scopeName={scopeName} onClose={() => setShowCreate(false)} onCreate={createAndAssign} />}
      <div className="space-y-1.5">
        <div className="text-xs uppercase tracking-wider text-[#000000] mb-1">Assigned ({assigned.length})</div>
        {assigned.map((a) => (
          <div key={a.id} data-testid={`assigned-${cfg.key}-${a.id}`} className="flex items-center justify-between rounded-lg border border-[#E4E4E7] px-2.5 py-1.5 text-sm">
            <span>{a.name}</span>
            <button data-testid={`unassign-${cfg.key}-${a.id}`} onClick={() => unassign(a.id)} className="text-[#FF5100]"><X size={14} weight="bold" /></button>
          </div>
        ))}
        {assigned.length === 0 && (fallback || <div className="text-sm text-[#000000]">No {cfg.nounPlural} yet.</div>)}
      </div>
    </div>
  );
}

function CreateStaffInline({ cfg, scopeName, onClose, onCreate }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("Welcome@123");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [added, setAdded] = useState([]);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await onCreate({ first_name: firstName.trim(), last_name: lastName.trim() || null, email: email.trim().toLowerCase(), password, role: cfg.createRole });
      setAdded((prev) => [...prev, `${firstName.trim()} ${lastName.trim()}`.trim()]);
      setFirstName(""); setLastName(""); setEmail(""); setPassword("Welcome@123");
      setBusy(false);
    } catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid={`create-${cfg.key}-inline`} className="rounded-xl border border-[#84CC16] bg-[#F7FEE7] p-3 mb-3 flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm text-[#3F6212]">New {cfg.noun} · {scopeName}</div>
        <button type="button" data-testid={`create-${cfg.key}-close`} onClick={onClose} className="text-[#3F6212] hover:opacity-70"><X size={16} weight="bold" /></button>
      </div>
      <p className="text-[11px] text-[#3F6212]/80 -mt-1">Creates &amp; assigns right away. Keep adding — new {cfg.nounPlural} appear in the list below. Click <b>Done</b> when finished.</p>
      {added.length > 0 && (
        <div data-testid={`create-${cfg.key}-added`} className="rounded-lg bg-white/70 border border-[#D9F99D] px-2.5 py-1.5 text-xs text-[#3F6212]">
          Added this session: {added.join(", ")}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input data-testid={`create-${cfg.key}-first-name`} className="input-base w-full" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
        <input data-testid={`create-${cfg.key}-last-name`} className="input-base w-full" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <input data-testid={`create-${cfg.key}-email`} type="email" className="input-base w-full" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <input data-testid={`create-${cfg.key}-password`} className="input-base w-full" placeholder="Temporary password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} required />
      {err && <div data-testid={`create-${cfg.key}-error`} className="text-[#FF5100] text-xs">{err}</div>}
      <div className="flex gap-2">
        <button data-testid={`create-${cfg.key}-submit`} type="submit" disabled={busy || !firstName.trim() || !email.trim()} className="btn-primary flex-1 text-sm">
          {busy ? "Creating…" : "Create & add another"}
        </button>
        <button data-testid={`create-${cfg.key}-done`} type="button" onClick={onClose} className="btn-ghost px-3 text-sm">Done</button>
      </div>
    </form>
  );
}


// Board-level kill switch for the AI Coach chat: default ON; switch OFF for boards that
// haven't signed off on the AI disclosure yet. Blocks the API and hides the button for their students.
function CoachToggle({ board, onChanged }) {
  const [busy, setBusy] = useState(false);
  const on = board.coach_enabled !== false;
  const flip = async () => {
    setBusy(true);
    try {
      await api.patch(`/boards/${board.id}`, { coach_enabled: !on });
      toast.success(`AI Coach ${!on ? "enabled" : "disabled"} for ${board.name}`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const signoff = board.coach_signoff;
  const record = async () => {
    const name = window.prompt("Who at the board approved the AI Coach? (name & title — e.g. 'Jane Doe, Superintendent')");
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/boards/${board.id}/coach-signoff`, { approved_by: name.trim() });
      const emailed = data?.receipt_emailed_to || [];
      toast.success(emailed.length
        ? `Sign-off recorded — Coach is ON. Receipt emailed to ${emailed.join(", ")}`
        : "Sign-off recorded — Coach is ON for this board (no board admin email on file, use the receipt link to download)");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const downloadReceipt = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/boards/${board.id}/coach-signoff.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-coach-consent-${(board.name || "board").replace(/[^a-z0-9 -]/gi, "").trim().replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const clearSignoff = async () => {
    if (!window.confirm("Remove this board's AI Coach sign-off record?")) return;
    setBusy(true);
    try {
      await api.delete(`/boards/${board.id}/coach-signoff`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-2.5 pb-2.5 border-b border-[#E4E4E7]" data-testid={`coach-toggle-row-${board.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-bold">AI Coach chat</div>
          <div className="text-[11px] text-[#000000]">Students can ask the AI Coach for advice. Turn off until this board signs off on the AI disclosure.</div>
        </div>
        <button data-testid={`coach-toggle-${board.id}`} onClick={flip} disabled={busy}
          className="shrink-0 rounded-full px-3 py-1 text-xs font-bold border transition-colors cursor-pointer"
          style={{ background: on ? "#CCFF00" : "#fff", color: on ? "#1F2937" : "#52525B", borderColor: on ? "#CCFF00" : "#C4C8CE" }}>
          {on ? "ON" : "OFF"}
        </button>
      </div>
      {signoff ? (
        <div className="text-[11px] mt-1.5 text-[#4D7C0F]" data-testid={`coach-signoff-${board.id}`}>
          ✓ <b>Board approved:</b> {signoff.approved_by} — {(signoff.at || "").slice(0, 10)}
          <span className="text-[#71717A]"> (recorded by {signoff.recorded_by_name})</span>
          <button onClick={downloadReceipt} disabled={busy} data-testid={`coach-signoff-receipt-${board.id}`}
            className="ml-2 text-[10px] font-bold text-[#4D7C0F] underline bg-transparent border-0 cursor-pointer">receipt (PDF)</button>
          <button onClick={clearSignoff} disabled={busy} data-testid={`coach-signoff-clear-${board.id}`}
            className="ml-2 text-[10px] text-[#A1A1AA] underline bg-transparent border-0 cursor-pointer">remove</button>
        </div>
      ) : (
        <button onClick={record} disabled={busy} data-testid={`coach-signoff-record-${board.id}`}
          className="text-[11px] mt-1.5 font-bold text-[#4D7C0F] underline bg-transparent border-0 cursor-pointer p-0">
          Record board sign-off (proof of consent — also turns Coach ON)
        </button>
      )}
    </div>
  );
}


// Board-level policy switch: lets this board's students & staff download the shareable
// congratulations image. Default OFF for the whole school system; board admins + super admin flip it.
function ShareAchievementToggle({ board, onChanged }) {
  const [busy, setBusy] = useState(false);
  const on = !!board.share_achievement_enabled;
  const flip = async () => {
    setBusy(true);
    try {
      await api.patch(`/boards/${board.id}`, { share_achievement_enabled: !on });
      toast.success(`Achievement sharing ${!on ? "enabled" : "disabled"} for ${board.name}`);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-2 mb-2.5 pb-2.5 border-b border-[#E4E4E7]" data-testid={`share-toggle-row-${board.id}`}>
      <div className="min-w-0">
        <div className="text-xs font-bold">Achievement sharing</div>
        <div className="text-[11px] text-[#000000]">Students & staff can download a congratulations image (no school branding, no links on it).</div>
      </div>
      <button data-testid={`share-toggle-${board.id}`} onClick={flip} disabled={busy}
        className="shrink-0 rounded-full px-3 py-1 text-xs font-bold border transition-colors cursor-pointer"
        style={{ background: on ? "#CCFF00" : "#fff", color: on ? "#1F2937" : "#52525B", borderColor: on ? "#CCFF00" : "#C4C8CE" }}>
        {on ? "ON" : "OFF"}
      </button>
    </div>
  );
}
